/**
 * Browser-sichere Werte des Team-Chats (Story 24.1, AD-1 Unterpfad `./values`): Texte, Namensregel der Kanäle, Arten.
 * Keine Datenbank, keine Uhr, kein Netz.
 */

export const CHANNEL_KINDS = ['public', 'private'] as const;
/** UUIDs haben eine Schreibweise in Speicherpfaden, Sperren und Mengenvergleichen. */
export const normalizeChatId = (id: string): string => id.toLowerCase();
export const CONVERSATION_KINDS = [...CHANNEL_KINDS, 'dm', 'group_dm'] as const;
export type ChannelKind = (typeof CONVERSATION_KINDS)[number];
export const isDirectConversation = (kind: ChannelKind): boolean => kind === 'dm' || kind === 'group_dm';

export const MEMBER_ROLES = ['owner', 'member'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

/** Entitäten im Protokoll (`audit_log.entity_type`) und Aggregate der Events. */
export const CHANNEL_ENTITY = 'chat_channel';
export const MESSAGE_ENTITY = 'chat_message';

/** Name des Kanals für alle (Migration 0564). */
export const GENERAL_CHANNEL_NAME = 'allgemein';

/** Kanalname ohne „#“: Kleinbuchstaben (auch Umlaute), Ziffern, Bindestrich, Unterstrich; gleiche Regel wie Migration 0564. */
export const CHANNEL_NAME_PATTERN = /^[a-z0-9äöüß][a-z0-9äöüß_-]{0,79}$/;
export const CHANNEL_NAME_MAX = 80;
export const CHANNEL_TOPIC_MAX = 250;

/**
 * Aus dem Getippten einen gültigen Kanalnamen machen (wie Slack): „Einkauf intern“ → `einkauf-intern`, „#Vertrieb“ →
 * `vertrieb`. Leerzeichen und Punkte werden Bindestriche, andere Zeichen fallen weg, doppelte Bindestriche werden einer.
 * Leer, wenn nichts Gültiges übrig bleibt.
 */
export function normalizeChannelName(input: string): string {
  return input
    .normalize('NFC')
    .toLowerCase()
    .replace(/^[#\s]+/, '')
    .replace(/[\s.]+/g, '-')
    .replace(/[^a-z0-9äöüß_-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^[-_]+/, '')
    .slice(0, CHANNEL_NAME_MAX)
    .replace(/-+$/, '');
}

/** „1 Mitglied“ / „2 Mitglieder“. */
export function memberCountText(count: number): string {
  return count === 1 ? '1 Mitglied' : `${count.toLocaleString('de-DE')} Mitglieder`;
}

export const CHAT_TEXTS = {
  nameMissing: 'Gib dem Kanal einen Namen.',
  nameInvalid: 'Nur Kleinbuchstaben, Ziffern, Bindestrich und Unterstrich – höchstens 80 Zeichen.',
  nameTaken: (name: string) => `Den Kanal #${name} gibt es schon. Tritt ihm bei oder wähle einen anderen Namen.`,
  topicTooLong: `Das Thema hat höchstens ${CHANNEL_TOPIC_MAX} Zeichen.`,
  memberUnknown: 'Diese Person gibt es nicht oder sie ist gesperrt.',
  summary: (count: number) => `${count} Angaben sind noch nicht richtig.`,
  channelNotFound: 'Diesen Kanal gibt es nicht oder du bist dort kein Mitglied.',
  notMember: 'Du bist in diesem Kanal kein Mitglied. Tritt bei, um die Nachrichten zu sehen.',
  generalNoLeave: 'Aus #allgemein kann niemand austreten – dort ist das ganze Team.',
  generalNoJoin: 'In #allgemein bist du schon – dort ist das ganze Team.',
  privateNoJoin: 'Diesen Kanal gibt es nicht oder du bist dort kein Mitglied.',
  lastMember: 'Du bist das letzte Mitglied dieses privaten Kanals. Ohne dich wäre er für alle verloren.',
  messageEmpty: 'Schreib erst etwas.',
  invalidChars: 'Der Text enthält ein unzulässiges Steuerzeichen.',
  messageTooLong: (max: number) => `Eine Nachricht hat höchstens ${max.toLocaleString('de-DE')} Zeichen.`,
  messageNotFound: 'Diese Nachricht gibt es nicht oder du bist im Kanal kein Mitglied.',
} as const;

export * from './reference-values.ts';
export * from './references.ts';
export { CHAT_EMOJIS, isChatEmoji } from './emoji-values.ts';

/** Nur dieser ERP-Link ist zum Weitergeben bestimmt, nie ein Storage-Link. */
export interface ChatFileView {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  url: string;
}
